<template>
  <section class="responsibility-badge-holders-tab">
    <div v-if="showAppointAction" class="responsibility-badge-holders-tab__actions">
      <Button
        class="appoint-badge-btn"
        label="Appoint as"
        normal="normal-hover"
        @onClick="showAppointDialog = true"
      />
    </div>
    <AppointBadgeDialog
      v-if="showAppointAction"
      v-model="showAppointDialog"
      :accountId="appointAccountId"
      :responsibilityId="responsibilityId"
      :responsibilityName="responsibilityData?.name || ''"
      @appointed="getBadges"
    />
    <ResponsibilityBadgeHoldersTable
      :responsibility-id="responsibilityId"
      :items="items"
      :items-count="totalCount"
      :filter-levels="filterLevels"
      :badge-level-counts="badgeLevelCounts"
      :on-refresh-badges="getBadges"
      :loading="badgesLoading"
      @removeBadge="handleRemoveBadge"
      @filtersChanged="handleFiltersChanged"
      @onActionClick="openForceBadgeModal"
      @chatter-click="handleBadgeChatterClick"
    />

    <ChatterDrawer
      :drawer="showBadgeChatterDrawer"
      type="Badge"
      :typeValue="selectedBadgeChatter?.badge?.level || selectedBadgeChatter?.level || ''"
      :listData="selectedBadgeChatter"
      object-type="badge"
      :object-id="selectedBadgeChatter?.id ?? null"
      :badgeStatus="
        selectedBadgeChatter?.badge?.dot || getBadgeDotFromStatus(selectedBadgeChatter?.status)
      "
      iconName="org_icon_responsibility"
      :chatterList="badgeChatterData"
      :totalCheckpointChatter="totalBadgeChatter"
      @closeDrawer="closeBadgeChatterDrawer"
      @requestMoreCheckpointsChatter="loadMoreBadgeChatter"
      @toggle-badge-status="handleToggleBadgeStatus"
    />

    <EmployeeModal
      :showStep="1"
      :dialog="showBadgeDialog"
      @onAction="addForceBadgeData"
      @openEvalAction="openEvalResponse"
      @back="backForceBadge"
      label="Employee"
      :name="responsibilityData?.name"
      :items="badgeEmployeeData"
      :activeCheckpoint="activeCheckPoints"
      :value="[1]"
      :enableEmployeePagination="true"
      :empForceBadgeClass="true"
      :key="employeeModalRenderKey"
      :responsibility="formattedResponsibilityData"
      @removeBadge="handleRemoveBadge"
      :isAssigningBadge="isAssigningBadge"
      :isArchitectWillingToCoach="actingUserArchitectWillCoach"
    />

    <PickEmployeeToIssueABadgeDialog
      :dialog="showPickEmployeeDialog"
      :items="badgeEmployeeData"
      @back="showPickEmployeeDialog = false"
      @onAction="handlePickEmployeeAction"
    />

    <RemoveBadgeDialog
      v-model="showRemoveBadgeDialog"
      :badgeName="selectedBadgeForRemoval?.badgeName || selectedBadgeForRemoval?.level || ''"
      :responsibilityName="props.responsibilityData?.name || ''"
      :employeeName="selectedBadgeForRemoval?.employeeName || ''"
      @remove="handleConfirmRemoveBadge"
      @close="handleCloseRemoveBadgeDialog"
    />

    <ReasonModal
      :showDialog="showReasonModal"
      title="Remove Badge"
      description="Please provide a reason for removing this badge."
      btnLabel="Remove Badge"
      placeHolder="Enter reason for removal..."
      :btnDanger="true"
      v-model="removalReason"
      @onClose="handleCloseReasonModal"
      @onAction="handleConfirmRemovalWithReason"
    />

    <EvalResponsibilityModal
      v-if="openEvalResponsibility"
      :dialog="openEvalResponsibility"
      :checkpointsData="checkpointsData"
      :evalModalHeaderState="evalModalHeaderState"
      :evalModalEmpState="evalModalEmpState"
      :currentResponsibilityId="responsibilityId"
      @closeModal="openEvalResponsibility = false"
      @updateEvaluationCheckPointData="updateEvaluationCheckPointData"
      @reloadData="updateEvaluationCheckPointData"
      @grantBadge="grantOrForceBadge"
      @removeBadge="removeBadgeByResponsibilityId"
      @reloadBadgeHolderItems="reloadBadgeHolderItems"
      @upgradeBadge="upgradeBadge"
      @showAwardBadge="showAwardBadge"
    />

    <BadgeConfirmationDialog
      :isVisible="showBadgeConfirmationDialog"
      :badgeData="badgeData"
      :badgeType="badgeData?.payload?.issued_method == 'given' ? 'Give' : 'Award'"
      :responsibilityName="props.responsibilityData?.name || ''"
      :employee="{
        name: `${evalModalEmpState?.firstName} ${evalModalEmpState?.lastName}`,
        avatar: evalModalEmpState?.photoUrl,
      }"
      :level="getBadgeLevel(badgeData?.payload?.level)"
      @close="showBadgeConfirmationDialog = false"
      :currentEmployee="evalModalEmpState"
      @onClick="onClick"
    />

    <AwardBadgeDialog
      :employee="awardBadgeEmployeeForDialog"
      :level="awardBadgeLevel"
      :isVisible="showAwardBadgeModal"
      @skip="handleSkipAwardBadge"
      @close="handleCloseAwardBadgeDialog"
      @activate="awardBadge"
      :currentEmployee="awardBadgeCurrentEmployee"
      :eligibility-by-access-null="true"
      :responsibilityName="props.responsibilityData?.name || ''"
    />
    <ChecklistInspectionRightPane
      :open="showInspectionPane"
      :employee="inspectionEmployee"
      :readiness-chip="inspectionReadinessChip"
      :responsibilityAssignment="inspectionResponsibilityAssignment"
      :responsibilityCheckpoints="inspectionResponsibilityCheckpoints"
      :items="inspectionChecklistItems"
      :has-active-checkpoints="(props.responsibilityData?.active_checkpoints_count || 0) > 0"
      :award-dialog-request="inspectionAwardDialogRequest"
      :current-employee="inspectionAwardBadgeDialogCurrentEmployee"
      :award-eligibility-by-access-null="true"
      @close="closeInspectionPane"
      @removeBadge="processRemoveBadgeFromInspection"
      @issueBadge="handleInspectionIssueBadge"
      @onPass="handleCheckpointPass"
      @onFail="handleCheckpointFail"
      @onNotApplicable="handleCheckpointNotApplicable"
      @onClose="handleCheckpointClose"
      @badgeAwarded="handleBadgeAwarded"
      @expiration-synced="handleInspectionBadgeExpirationSynced"
    />

    <AsCoachInspectWorkspace as-drawer :inspect="inspectCtx" />

    <GiveBadgeDialog
      :value="showGiveBadgeDialog"
      :description="giveBadgeDialogDescription"
      :employeeName="inspectionEmployee?.fullName || ''"
      :responsibilityName="props.responsibilityData?.name || ''"
      :canNotify="canNotifyForIssueBadge"
      :defaultLevel="giveBadgeDialogDefaultLevel"
      :currentBadgeLevel="giveBadgePickerCurrentLevel"
      :lockedLevel="giveBadgeDialogLockedLevel"
      @input="onGiveBadgeDialogInput"
      @give-badge="confirmGiveBadge"
      @close="showGiveBadgeDialog = false"
    />

    <UpgradeBadgeDialog
      :value="showUpgradeBadgeDialog"
      :employeeName="inspectionEmployee?.fullName || ''"
      :responsibilityName="props.responsibilityData?.name || ''"
      :canNotify="canNotifyForIssueBadge"
      :currentBadgeLevel="upgradePickerCurrentLevel"
      @input="showUpgradeBadgeDialog = $event"
      @give-badge="confirmUpgradeBadge"
      @close="showUpgradeBadgeDialog = false"
    />

    <HasMasterBadgeDialog
      :show="showHasMasterBadgeDialog"
      @close="showHasMasterBadgeDialog = false"
    />

    <ReasonModal
      :showDialog="showCheckpointReasonModal"
      :title="checkpointReasonModalType === 'fail' ? 'Fail Checkpoint' : 'Mark Not Applicable'"
      :description="
        checkpointReasonModalType === 'fail'
          ? 'Please provide a reason why this checkpoint was failed.'
          : 'Please provide a reason why this checkpoint is not applicable.'
      "
      :btnLabel="checkpointReasonModalType === 'fail' ? 'Fail' : 'Mark N/A'"
      :btnColor="checkpointReasonModalType === 'fail' ? '#D92D20' : '#504D56'"
      :btnDanger="checkpointReasonModalType === 'fail'"
      :value="checkpointReasonText"
      @input="checkpointReasonText = $event"
      @onAction="confirmCheckpointReason"
      @onClose="closeCheckpointReasonModal"
    />
  </section>
</template>

<script setup>
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { ref, reactive, getCurrentInstance, computed, watch, nextTick } from "vue";
import { getBadgeIconNameFromCause } from "../utils/proficiencyBadgeView";
import {
  getListofResponsibilityBadge,
  deleteResponsibilityBadge,
  createProficiencyBadge,
  getListActiveCheckpointsEvalV2,
  getEmployeeBadges,
  getResponsibilityChecklistV2,
  getAssignmentEval,
  passCheckPointEvaluation,
  failCheckPointEvaluation,
  notApplicableCheckPointEvaluation,
  resetCheckPointEvaluation,
  getChatterList,
} from "@/api";
import { formatOrganizationEmployeeChatterNote } from "@/utils/chatterUtils";
import { useProficiencyBadgeExpirationStore } from "@/store/pinia/proficiencyBadgeExpiration";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import {
  mergeAssignmentWithEvaluate,
  unwrapEvaluateResponse,
} from "@/utils/mergeAssignmentEvaluate";
import {
  badgeMatchesResponsibility,
  mapBadgeRowToInspectionChip,
  pickBadgeForResponsibility,
  isLevelCompleteForAward,
} from "@/utils/inspectionBadgeSync";
import {
  extractCreatedProficiencyBadgeId,
  proficiencyCheckpointErrorMessage,
} from "@/utils/proficiencyBadgeHelpers";
import ResponsibilityBadgeHoldersTable from "@components/ResponsibilityBadgeHoldersTable/ResponsibilityBadgeHoldersTable.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import EmployeeModal from "@components/EmployeeModal/EmployeeModal.vue";
import EvalResponsibilityModal from "@components/EvalResponsibilityModal/EvalResponsibilityModal.vue";
import BadgeConfirmationDialog from "@components/BadgeConfirmationDialog/BadgeConfirmationDialog.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import { useBadgeHolders } from "@components/ResponsibilityBadgeHoldersTable/composables/useBadgeHolders";
import { useBadgeHoldersViewState } from "@components/ResponsibilityBadgeHoldersTable/composables/useBadgeHoldersViewState";
import PickEmployeeToIssueABadgeDialog from "@components/PickEmployeeToIssueABadgeDialog/PickEmployeeToIssueABadgeDialog.vue";
import ChecklistInspectionRightPane from "@components/ChecklistInspectionRightPane/ChecklistInspectionRightPane.vue";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
import Button from "@components/buttons/Button.vue";
import AppointBadgeDialog from "@components/AppointBadgeDialog/AppointBadgeDialog.vue";
import { isAppointedBadge, isCurrentUserAdmin } from "@components/utils/appointedBadge";
import { useSelfAppointPromptStore } from "@/store/pinia/selfAppointPrompt";
import UpgradeBadgeDialog from "@components/UpgradeBadgeDialog/UpgradeBadgeDialog.vue";
import HasMasterBadgeDialog from "@components/HasMasterBadgeDialog/HasMasterBadgeDialog.vue";
import { getBadgeLevelCode } from "@components/utils/badgeLevelLabels";
import AsCoachInspectWorkspace from "@components/AsCoachInspectWorkspace/AsCoachInspectWorkspace.vue";
import { useCoachInspect } from "@components/AsCoachInspectWorkspace/composables/useCoachInspect";
import { LD_FLAG_IDEA_MDP_626 } from "@components/constants/launchDarklyFlagKeys";
import { fetchActingUserArchitectWillCoachForResponsibility } from "@components/utils/actingUserArchitectWillCoach";

const { proxy } = getCurrentInstance();
const proficiencyBadgeExpiration = useProficiencyBadgeExpirationStore();
const selfAppointPromptStore = useSelfAppointPromptStore();

const showAppointDialog = ref(false);
const showAppointAction = computed(
  () => proxy.$store?.getters?.["featureFlags/ideaMDP641"] === true && isCurrentUserAdmin()
);
const appointAccountId = computed(() => JSON.parse(localStorage.getItem("currentAccountID")));
watch(
  () => selfAppointPromptStore.refreshGeneration,
  () => getBadges()
);

const props = defineProps({
  responsibilityId: {
    type: [Number, String],
    required: true,
  },
  responsibilityData: {
    type: Object,
    required: false,
  },
  status: {
    type: String,
    required: false,
    validator: (value) => {
      if (!value) return true; // Allow undefined/null
      return ["Current", "Expired", "Terminated", "Removed"].includes(value);
    },
  },
  filterLevels: {
    type: Array,
    default: () => [],
  },
  /** Position name for inspection form header (e.g. from route query when opened from Position > Role > Responsibility). */
  positionName: {
    type: String,
    default: "",
  },
  /** Role name for inspection form header. */
  roleName: {
    type: String,
    default: "",
  },
  positionCode: {
    type: String,
    default: "",
  },
  roleCode: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["badgeCountsUpdated", "inspectionOpen", "inspectionClose"]);

// Composables (module singleton â€” reset when tab mounts or responsibility changes so list matches route)
const badgeHolders = useBadgeHolders();
const viewState = useBadgeHoldersViewState();

function resetSharedBadgeHoldersUiStateForResponsibility() {
  badgeHolders.setPaginationDataOffset(0);
  badgeHolders.setPaginationDataLimit(10);
  badgeHolders.setOrderingDataField("issued_to__first_name");
  badgeHolders.setStatusFilter([]);
  badgeHolders.setLevelFilter([]);
  viewState.state.filters.searchText = "";
  viewState.state.filters.values = ["Current"];
}

// Singleton composables survive v-if — reset before table's immediate filter fires (avoids stale pagination + double fetch with onMounted)
resetSharedBadgeHoldersUiStateForResponsibility();

// State
const items = ref([]);
const totalCount = ref(0);
const showRemoveBadgeDialog = ref(false);
const selectedBadgeForRemoval = ref(null);
const selectedStatusFilters = ref([]);
const selectedLevelFilters = ref([]);
const showBadgeDialog = ref(false);
const badgeEmployeeData = ref([]);
const activeCheckPoints = ref(0);
const employeeModalRenderKey = ref(0);
const isAssigningBadge = ref(false);
const actingUserArchitectWillCoach = ref(false);

async function loadActingUserArchitectWillCoach() {
  const responsibilityId = props.responsibilityId;
  const responsibilityCode = props.responsibilityData?.code || props.responsibilityData?.code_str;
  actingUserArchitectWillCoach.value = await fetchActingUserArchitectWillCoachForResponsibility(
    responsibilityId,
    { responsibilityCode }
  );
}

watch(
  () => props.responsibilityId,
  (id, prev) => {
    if (id == null || id === "") return;
    if (prev != null && String(prev) !== String(id)) {
      resetSharedBadgeHoldersUiStateForResponsibility();
    }
    loadActingUserArchitectWillCoach();
  }
);

loadActingUserArchitectWillCoach();
const showPickEmployeeDialog = ref(false);
const openEvalResponsibility = ref(false);
const checkpointsData = ref([]);
const evalModalHeaderState = ref(null);
const evalModalEmpState = ref(null);
const badgeData = ref(null);
const showBadgeConfirmationDialog = ref(false);
const showAwardBadgeModal = ref(false);
const awardBadgeLevel = ref(null);
const badgeFrameItems = ref([]);
const showReasonModal = ref(false);
const removalReason = ref("");
const badgeToRemove = ref(null);

// Badge chatter drawer state
const showBadgeChatterDrawer = ref(false);
const selectedBadgeChatter = ref(null);
const badgeChatterData = ref([]);
const totalBadgeChatter = ref(0);
const badgeChatterPage = ref(1);
const badgeChatterLoading = ref(false);

// Inspection right pane state
const showInspectionPane = ref(false);
const inspectionEmployee = ref(null);
const inspectionResponsibilityAssignment = ref(null);
const inspectionResponsibilityCheckpoints = ref([]);
const inspectionChecklistItems = ref([]);
const currentInspectionEmployeeId = ref(null);
const inspectionCurrentBadgeLevel = ref(null);
const showGiveBadgeDialog = ref(false);
const showUpgradeBadgeDialog = ref(false);
const showHasMasterBadgeDialog = ref(false);
const inspectionAwardDialogRequest = ref(null);
const removeBadgeDialogFromInspection = ref(false);
const giveBadgeDialogLockedLevel = ref(null);
const giveBadgeDialogDefaultLevel = ref("apprentice");
/** Mirrors Path `showGiveBadgeDialogFromInspection` â€” drives give-dialog current tier from pane badge. */
const showGiveBadgeDialogFromInspection = ref(false);

const PICKER_LEVEL_ORDER = ["apprentice", "professional", "coach", "master"];

// As Coach inspect drawers (idea-MDP-626). Legacy right pane stays on the flag-off path.
const coachInspect = useCoachInspect({
  onListRefresh: async () => {
    await getBadges();
    emit("badgeCountsUpdated");
    updateEmployeeList();
  },
});
const inspectCtx = reactive({ ...coachInspect });

async function isCoachInspectDrawerEnabled() {
  try {
    return (await proxy.$getFlag?.(LD_FLAG_IDEA_MDP_626, false)) === true;
  } catch {
    return false;
  }
}

const COACH_LEVEL_LABELS = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };

function coachBadgeLevelLabel(level) {
  if (level == null || level === "") return "Novice";
  const label = COACH_LEVEL_LABELS[level] || String(level);
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
}

/** Coach-queue-shaped row so the shared inspect composable can run on a Badge Holders pick. */
function buildCoachInspectRow({
  employee,
  fullName,
  firstName,
  lastName,
  assignment,
  badge,
  badges,
}) {
  return {
    id: employee.id,
    user: {
      id: employee.id,
      name: fullName,
      email: employee.email || "",
      avatar: employee.avatar || employee.photoUrl || null,
      position: employee.primary_position?.name || "",
      department: "",
    },
    responsibilityAssignments: {
      responsibility_id: assignment.responsibilityId,
      responsibility_code: assignment.responsibilityCode,
      responsibility_name: assignment.responsibilityName,
      responsibility_importance: assignment.responsibilityImportance,
      role_code: assignment.roleCode,
      role_name: assignment.roleName,
      role_importance: assignment.roleImportance,
      position_code: assignment.positionCode,
      position_name: assignment.positionName,
      position_importance: assignment.positionImportance,
      position_commitment: assignment.positionCommitment,
      assigned_on: assignment.assignedOn,
      new_duration: assignment.newDuration,
    },
    badge: {
      id: badge?.id || null,
      level: coachBadgeLevelLabel(badge?.level),
      status: badge?.status || "No Dot",
      checkpoints: badge?.checkpoints || [],
      issued_on: badge?.issued_on ?? null,
      issued_method: badge?.issued_method ?? null,
      expiration_date: badge?.expiration_date ?? null,
      expires_in_days_from_becoming_obsolete: badge?.expires_in_days_from_becoming_obsolete ?? null,
      has_expiration_if_obsolete: !!badge?.has_expiration_if_obsolete,
      retries_to_expire_in_days: badge?.retries_to_expire_in_days ?? undefined,
    },
    originalEmployee: {
      id: employee.id,
      employee_id: employee.id,
      first_name: firstName,
      last_name: lastName,
      avatar: employee.avatar || employee.photoUrl || null,
      email: employee.email || "",
      access: employee.access ?? null,
      primary_position: employee.primary_position || null,
      badges,
    },
  };
}

/** Same normalization as assignment `responsibilityCode` and EmployeeDetail pane matching â€” first `#digits` in code_str, else `#` + numeric code. */
function normalizedResponsibilityCodeForBadgePick(detail) {
  if (!detail) return "";
  const extractHashCode = (str) => {
    if (!str && str !== 0) return "";
    const m = String(str).match(/#\d+/);
    return m ? m[0] : String(str);
  };
  return (
    extractHashCode(detail.code_str) ||
    (detail.code != null && detail.code !== "" ? `#${String(detail.code).replace(/^#/, "")}` : "")
  );
}

function responsibilityMatchContextFromDetail(detail) {
  if (!detail) return {};
  const code = normalizedResponsibilityCodeForBadgePick(detail);
  const name =
    detail.name != null && String(detail.name).trim() !== "" ? String(detail.name).trim() : "";
  return {
    ...(code ? { responsibilityCode: code } : {}),
    ...(name ? { responsibilityName: name } : {}),
  };
}

function normalizeProficiencyLevelForPicker(level) {
  if (level == null || level === "") return null;
  if (typeof level === "number" && level >= 1 && level <= 4) {
    return PICKER_LEVEL_ORDER[level - 1];
  }
  const code = getBadgeLevelCode(level);
  return PICKER_LEVEL_ORDER.includes(code) ? code : null;
}

function inspectionBadgeMatchContext() {
  const fromProps = responsibilityMatchContextFromDetail(props.responsibilityData);
  const a = inspectionResponsibilityAssignment.value;
  if (a?.responsibilityCode) {
    return { ...fromProps, responsibilityCode: String(a.responsibilityCode) };
  }
  return fromProps;
}

// Checkpoint reason modal state
const showCheckpointReasonModal = ref(false);
const checkpointReasonModalType = ref("fail");
const pendingCheckpointReasonItem = ref(null);
const checkpointReasonText = ref("");

// Badge level counts from responsibilityData
const badgeLevelCounts = computed(() => {
  const data = props.responsibilityData;
  const counts = {
    apprentice: data?.badge_apprentice_holder_count || 0,
    professional: data?.badge_proficient_holder_count ?? data?.badge_professional_holder_count ?? 0,
    coach: data?.badge_coach_holder_count || 0,
    master: data?.badge_architect_holder_count ?? data?.badge_master_holder_count ?? 0,
  };
  return counts;
});

// Award badge dialog: EvalResponsibilityModal flow (inspection pane uses in-pane AwardBadgeDialog via award-dialog-request)
const awardBadgeEmployeeForDialog = computed(() => ({
  name:
    `${evalModalEmpState.value?.firstName || ""} ${
      evalModalEmpState.value?.lastName || ""
    }`.trim() || "Employee",
  avatar: evalModalEmpState.value?.photoUrl || "",
}));
const awardBadgeCurrentEmployee = computed(() => evalModalEmpState.value);

// Give badge dialog: description when responsibility has no checklist (matches NewEmployeePathToReadinessView / EmployeeDetail)
const giveBadgeDialogDescription = computed(() => {
  const respName = props.responsibilityData?.name || "this responsibility";
  const empName = inspectionEmployee.value?.fullName || "the employee";
  return `Responsibility ${respName} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${empName}.`;
});

// Path to Readiness / EmployeeDetail: notify only when access is known and not suspended / revoked
const canNotifyForIssueBadge = computed(() => {
  const access = inspectionEmployee.value?.access;
  if (access == null) return false;
  if (access.is_suspended === true) return false;
  if (access.has_access === false) return false;
  return true;
});

const inspectionReadinessChip = computed(() => {
  if (!showInspectionPane.value) return null;
  const b = inspectionEmployee.value?.badge;
  return {
    level: b?.level ?? "Novice",
    dot: b?.dot ?? "Good Standing",
    id: b?.id ?? null,
  };
});

const inspectionAwardBadgeDialogCurrentEmployee = computed(() => {
  const e = inspectionEmployee.value;
  if (!e) return {};
  return {
    user: e.id,
    access: Object.prototype.hasOwnProperty.call(e, "access") ? e.access : null,
  };
});

/** Give dialog: from inspection use pane badge as current tier (Path giveBadgeDialogCurrentLevel). */
const giveBadgePickerCurrentLevel = computed(() => {
  if (showGiveBadgeDialogFromInspection.value) {
    return normalizeProficiencyLevelForPicker(inspectionEmployee.value?.badge?.level);
  }
  return inspectionCurrentBadgeLevel.value;
});

/** Upgrade dialog always receives a valid tier (avoids empty picker / invalid prop). */
const upgradePickerCurrentLevel = computed(() => inspectionCurrentBadgeLevel.value || "apprentice");

// Format responsibility data to ensure code is a string for EmployeeModal
const formattedResponsibilityData = computed(() => {
  if (!props.responsibilityData) return null;

  const data = { ...props.responsibilityData };

  // Ensure code is a string - extract from code_str or convert code number to string
  if (data.code_str) {
    // Extract code from code_str (e.g., "#123 - Name" -> "#123")
    const codeMatch = data.code_str.match(/#\d+/);
    if (codeMatch) {
      data.code = codeMatch[0];
    } else {
      // If no # found, try to extract just the number and add #
      const numMatch = data.code_str.match(/\d+/);
      data.code = numMatch
        ? `#${numMatch[0]}`
        : data.code
        ? String(data.code).startsWith("#")
          ? String(data.code)
          : `#${data.code}`
        : "";
    }
  } else if (data.code !== undefined && data.code !== null) {
    // Convert code to string format, ensuring it starts with #
    const codeStr = String(data.code);
    data.code = codeStr.startsWith("#") ? codeStr : `#${codeStr}`;
  } else {
    data.code = "";
  }

  return data;
});

const getBadgeDotFromStatus = (status) => {
  const statusMap = {
    given: "Given",
    good_standing: "Good Standing",
    obsolete: "Obsolete",
  };
  return statusMap[status] || "Good Standing";
};

const getBadgeIconFromCause = (cause) => getBadgeIconNameFromCause(cause);

/**
 * Responsibility templates expose `awarded_expiration_obsolete` (e.g. when_awarded window) on every
 * badge row. That value must not drive ReadinessBadge for `issued_method === "given"` badges, which
 * follow `when_given` (date-only) â€” otherwise ExpireIcon uses `if_obsolete` and obsolete-only UI fires incorrectly.
 */
const badgeRowUsesAwardedObsoleteTemplateFields = (item) =>
  String(item?.issued_method || "").toLowerCase() !== "given";

/** Active holder rows use Editable so ExpireIcon + remove match employee badge holders UI. */
const resolveBadgeHolderReadinessView = (item) => {
  if (item.is_current === false) {
    return item.cause ? "Editable" : "ReadOnly";
  }
  return "Editable";
};

const transformBadgeHolders = (results) => {
  if (!results || !Array.isArray(results)) return [];

  return results.map((item) => {
    const fullName = `${item.issued_to?.first_name || ""} ${
      item.issued_to?.last_name || ""
    }`.trim();
    const badgeView = resolveBadgeHolderReadinessView(item);

    const useAwardedObsoleteFields = badgeRowUsesAwardedObsoleteTemplateFields(item);
    const expiresInDaysFromObsolete =
      item.expires_in_days_from_becoming_obsolete != null
        ? item.expires_in_days_from_becoming_obsolete
        : useAwardedObsoleteFields && item.awarded_expiration_obsolete > 0
        ? item.awarded_expiration_obsolete
        : null;
    const hasExpirationIfObsolete =
      !!item.expires_when_obsolete ||
      (useAwardedObsoleteFields && item.awarded_expiration_obsolete > 0);

    return {
      id: item.id,
      is_current: item.is_current,
      issued_method: item.issued_method,
      user: {
        id: item.issued_to?.id,
        name: fullName,
        email: item.issued_to?.email || "",
      },
      employee: {
        id: item.issued_to?.id,
        name: fullName,
        avatar: item.issued_to?.avatar,
        title: item.issued_to?.title || null,
      },
      userStatus: item.issued_to?.current_status || "active",
      employeeStatus: item.issued_to?.current_status || "active",
      accountHover: true,
      badge: {
        level: item.level || "Novice",
        dot: getBadgeDotFromStatus(item.status),
        view: badgeView,
        expiration_date: item.expiration_date || null,
        expires_in_days_from_becoming_obsolete: expiresInDaysFromObsolete,
        has_expiration_if_obsolete: hasExpirationIfObsolete,
        upgraded: !item.is_current && !!item.cause,
        upgradedVariantIcon: getBadgeIconFromCause(item.cause),
        unread: 0,
        hideChatterIcon: true,
        lifecycleStatus: item.lifecycle_status || null,
        issuingMethod: item.issuing_method || null,
        endedOn: item.ended_on || item.removed_on || null,
        endedBy: item.removed_by || item.ended_by || null,
        endReason: item.end_reason || item.remove_reason || "",
      },
      assignedBy: item.issued_by
        ? {
            name: item.issued_by.name || "",
            picture: item.issued_by.picture || "",
            userType: "user",
            displayDate: item.issued_on || null,
          }
        : null,
      removedBy: item.removed_by
        ? {
            name: item.removed_by.name || "",
            picture: item.removed_by.picture || "",
            userType: "user",
            displayDate: item.removed_on || null,
          }
        : null,
      reason:
        item.remove_reason &&
        typeof item.remove_reason === "string" &&
        item.remove_reason.trim() !== ""
          ? item.remove_reason
          : "",
      status: item.status,
      cause: item.cause,
      issued_for: item.issued_for,
    };
  });
};

/** V2 list for EvalResponsibilityModal badge strip only â€” skip when modal is closed (avoids duplicate badge APIs). */
const refreshEvalModalBadgeFrameIfOpen = async () => {
  if (!openEvalResponsibility.value) return;
  await getResponsibilityBadges();
};

const badgesLoading = ref(false);

const getBadges = async () => {
  try {
    proxy.$loader.show();
    badgesLoading.value = true;
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const statusToUse =
      selectedStatusFilters.value.length > 0 ? selectedStatusFilters.value : props.status || null;
    const levelToUse =
      selectedLevelFilters.value.length > 0
        ? selectedLevelFilters.value
        : props.filterLevels && props.filterLevels.length > 0
        ? props.filterLevels.map((level) =>
            typeof level === "string" ? getBadgeLevelByLabel(level) : level
          )
        : null;

    const limit = badgeHolders.paginationDefaultParams.limit;
    const offset = badgeHolders.paginationDefaultParams.offset;
    const search = viewState.state.filters.searchText || null;
    const ordering = badgeHolders.orderingData.field || null;

    // Serialize params manually to handle arrays correctly (status=Current&status=Terminated)
    const queryParts = [];

    if (statusToUse !== null && statusToUse !== undefined) {
      const statusArray = Array.isArray(statusToUse) ? statusToUse : [statusToUse];
      statusArray.forEach((status) => {
        queryParts.push(`status=${encodeURIComponent(status)}`);
      });
    }

    if (levelToUse !== null && levelToUse !== undefined && levelToUse.length > 0) {
      const levelArray = Array.isArray(levelToUse) ? levelToUse : [levelToUse];
      levelArray.forEach((level) => {
        queryParts.push(`level=${encodeURIComponent(level)}`);
      });
    }

    if (limit !== null && limit !== undefined) {
      queryParts.push(`limit=${encodeURIComponent(limit)}`);
    }

    if (offset !== null && offset !== undefined) {
      queryParts.push(`offset=${encodeURIComponent(offset)}`);
    }

    if (search !== null && search !== undefined && search !== "") {
      queryParts.push(`employee_name=${encodeURIComponent(search)}`);
    }

    if (ordering !== null && ordering !== undefined) {
      queryParts.push(`ordering=${encodeURIComponent(ordering)}`);
    }

    // Build params object with serialized query string
    const params = queryParts.length > 0 ? { _query: queryParts.join("&") } : {};

    const response = await getListofResponsibilityBadge(
      accountId,
      props.responsibilityId,
      "responsibility",
      params
    );

    if (response.status === 200) {
      const transformedData = transformBadgeHolders(response.data.results || []);
      items.value = transformedData;
      totalCount.value = response.data.count || 0;
      badgeHolders.badgeHoldersCount.value = totalCount.value;
    } else {
      console.error("Error fetching badges:", response.data);
    }
  } catch (error) {
    console.error("Error fetching badges:", error);
  } finally {
    proxy.$loader.hide();
    badgesLoading.value = false;
  }
};

const handleFiltersChanged = (filters) => {
  if (Array.isArray(filters)) {
    selectedStatusFilters.value = filters;
    selectedLevelFilters.value = [];
  } else if (filters && typeof filters === "object") {
    selectedStatusFilters.value = filters.status || [];
    selectedLevelFilters.value = filters.level || [];
  } else {
    selectedStatusFilters.value = [];
    selectedLevelFilters.value = [];
  }
  badgeHolders.setPaginationDataOffset(0);
  getBadges();
};

const handleRemoveBadge = (item) => {
  if (!item || !item.id) {
    console.error("Invalid badge data:", item);
    proxy.$notifications?.error("Invalid badge data");
    return;
  }
  if (isAppointedBadge(item) && !isCurrentUserAdmin()) {
    proxy.$notifications?.error("Only administrators can revoke an appointed badge");
    return;
  }

  // Extract badge level and name
  const badgeLevel = item.badge?.level || item.level || "";
  const badgeLevelName = getBadgeLevelName(badgeLevel);

  selectedBadgeForRemoval.value = {
    id: item.id,
    badgeName: badgeLevelName,
    level: badgeLevel,
    employeeName: item.employee?.name || item.employeeName || "",
  };

  showRemoveBadgeDialog.value = true;
};

function getBadgeLevelName(level) {
  if (typeof level === "string") {
    // Capitalize first letter
    return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
  }
  const levelMap = {
    1: "Apprentice",
    2: "Professional",
    3: "Coach",
    4: "Master",
  };
  return levelMap[level] || "Apprentice";
}

const handleConfirmRemoveBadge = async ({ reason: removeReason }) => {
  if (
    !selectedBadgeForRemoval.value ||
    !selectedBadgeForRemoval.value.id ||
    !removeReason?.trim()
  ) {
    proxy.$notifications?.error("Please provide a reason for removing the badge");
    return;
  }

  const fromInspection = removeBadgeDialogFromInspection.value;
  try {
    proxy.$loader.setAppLoading(true);
    const payload = {
      badge: selectedBadgeForRemoval.value.id,
      method: "removed",
      reason: removeReason.trim(),
    };
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    await deleteResponsibilityBadge(accountId, selectedBadgeForRemoval.value.id, payload);

    proxy.$notifications?.success("Badge removed successfully");

    showRemoveBadgeDialog.value = false;
    selectedBadgeForRemoval.value = null;
    removeBadgeDialogFromInspection.value = false;

    if (fromInspection) {
      if (inspectionEmployee.value) {
        inspectionEmployee.value = { ...inspectionEmployee.value, badge: null };
      }
      inspectionCurrentBadgeLevel.value = null;
      await reloadInspectionCheckpoints(undefined, undefined, {
        skipInspectionBadgeSync: true,
      });
      await getBadges();
      emit("badgeCountsUpdated");
      updateEmployeeList();
      return;
    }

    await getBadges();
    await refreshEvalModalBadgeFrameIfOpen();
    emit("badgeCountsUpdated");
  } catch (error) {
    console.error("Error removing badge:", error);
    proxy.$notifications?.error("Error removing badge");
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const handleCloseRemoveBadgeDialog = () => {
  showRemoveBadgeDialog.value = false;
  selectedBadgeForRemoval.value = null;
  removeBadgeDialogFromInspection.value = false;
};

const openForceBadgeModal = () => {
  const data = props.responsibilityData;
  if (data) {
    activeCheckPoints.value =
      (data.active_apprentice_checkpoints || 0) +
      (data.active_coach_checkpoints || 0) +
      (data.active_master_checkpoints || 0) +
      (data.active_professional_checkpoints || 0);
    badgeEmployeeData.value = [];
    showPickEmployeeDialog.value = true;
  }
};

const backForceBadge = () => {
  showBadgeDialog.value = false;
};

function mergeEmployeeBadgeRowsForPick(apiRows, dialogBadgeList) {
  const out = Array.isArray(apiRows) ? [...apiRows] : [];
  if (!Array.isArray(dialogBadgeList) || dialogBadgeList.length === 0) {
    return out;
  }
  const seen = new Set(out.map((r) => r?.id).filter((id) => id != null && id !== ""));
  for (const b of dialogBadgeList) {
    if (b?.id != null && b.id !== "" && !seen.has(b.id)) {
      out.push(b);
      seen.add(b.id);
    }
  }
  return out;
}

const handlePickEmployeeAction = async ({ employee }) => {
  showPickEmployeeDialog.value = false;
  try {
    proxy.$loader?.show();
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const rawEmpId = employee?.id;
    const empId = rawEmpId != null && rawEmpId !== "" ? rawEmpId : null;
    if (empId == null) {
      proxy.$notifications?.error("Invalid employee selection");
      return;
    }
    const resId = props.responsibilityId;

    const fullName =
      employee.fullName ||
      `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
      employee.name ||
      "";
    const empFirstName = employee.firstName || employee.first_name || fullName.split(" ")[0] || "";
    const empLastName =
      employee.lastName || employee.last_name || fullName.split(" ").slice(1).join(" ") || "";

    // Load checkpoint evaluations and checklist in parallel
    const [evalResponse, checklistResponse, badgesResponse] = await Promise.allSettled([
      getListActiveCheckpointsEvalV2(accountId, resId, empId, 100, 0),
      getResponsibilityChecklistV2(accountId, resId, { status: "active" }),
      getEmployeeBadges(accountId, empId),
    ]);

    const evalResults =
      evalResponse.status === "fulfilled" ? evalResponse.value?.data?.results || [] : [];
    const responsibilityCheckpoints =
      checklistResponse.status === "fulfilled" ? checklistResponse.value?.data || [] : [];
    const employeeBadgesFromApi =
      badgesResponse.status === "fulfilled" ? badgesResponse.value?.data?.results || [] : [];

    const respData = props.responsibilityData;
    const matchCtx = responsibilityMatchContextFromDetail(respData);
    const mergedRows = mergeEmployeeBadgeRowsForPick(employeeBadgesFromApi, employee.badgeList);
    let currentBadge = pickBadgeForResponsibility(mergedRows, resId, matchCtx);
    if (
      !currentBadge &&
      employee.currentBadge &&
      badgeMatchesResponsibility(employee.currentBadge, resId, matchCtx)
    ) {
      currentBadge = employee.currentBadge;
    }

    inspectionEmployee.value = {
      id: employee.id,
      fullName: fullName || `${empFirstName} ${empLastName}`.trim(),
      photoUrl: employee.avatar || employee.photoUrl || "",
      position: employee.primary_position?.name || "",
      access: employee.access ?? null,
      badge: currentBadge ? mapBadgeRowToInspectionChip(currentBadge) : null,
    };

    let assignment = {
      responsibilityId: props.responsibilityId,
      responsibilityCode: normalizedResponsibilityCodeForBadgePick(respData),
      responsibilityName: respData?.name || "",
      responsibilityImportance: 3,
      roleCode: props.roleCode || "",
      roleName: props.roleName || "",
      roleImportance: 3,
      positionCode: props.positionCode || "",
      positionName: props.positionName || "",
      positionImportance: 3,
      positionCommitment: "Primary",
      assignedOn: null,
      newDuration: 15,
      show_codes: true,
    };

    try {
      const evalRes = await getAssignmentEval(accountId, resId, "responsibility");
      assignment = mergeAssignmentWithEvaluate(assignment, unwrapEvaluateResponse(evalRes));
    } catch {
      /* route props may still be enough; else responsibility-only header */
    }

    inspectionResponsibilityAssignment.value = assignment;

    currentInspectionEmployeeId.value = empId;
    inspectionCurrentBadgeLevel.value = normalizeProficiencyLevelForPicker(currentBadge?.level);

    // Employee has current Master badge for this responsibility â†’ show Has Master Badge dialog
    if (
      currentBadge &&
      currentBadge.is_current &&
      normalizeBadgeLevel(currentBadge.level) === "architect"
    ) {
      showHasMasterBadgeDialog.value = true;
      return;
    }

    // No active checkpoints: either Give (no badge) or Upgrade (badge already assigned)
    if (activeCheckPoints.value === 0) {
      showGiveBadgeDialogFromInspection.value = false;
      giveBadgeDialogLockedLevel.value = null;
      giveBadgeDialogDefaultLevel.value = "apprentice";
      if (currentBadge) {
        showUpgradeBadgeDialog.value = true;
      } else {
        showGiveBadgeDialog.value = true;
      }
      return;
    }

    if (await isCoachInspectDrawerEnabled()) {
      await coachInspect.openInspection(
        buildCoachInspectRow({
          employee,
          fullName: inspectionEmployee.value.fullName,
          firstName: empFirstName,
          lastName: empLastName,
          assignment,
          badge: currentBadge,
          badges: mergedRows,
        })
      );
      return;
    }

    // At least one active checkpoint: keep same behaviour â†’ open inspection pane
    const toTitleCaseStatus = (s) => {
      if (!s) return "Pending";
      return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };
    const levelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
    const checklistItems = evalResults.map((el) => ({
      id: el.checkpoint,
      // Inspection PK — the record checkpoint_evaluation chatter is keyed on.
      evaluation_id: el.id,
      checkpoint_id: el.checkpoint,
      responsibility_id: resId,
      employee_id: empId,
      level: levelMap[el.proficiency_level] || "Unknown",
      prefix: el.prefix || "",
      checkpoint: el.checkpoint_criteria || el.checkpoint_instructions || "",
      status: toTitleCaseStatus(el.status),
      inspected_by: el.evaluated_by?.name || null,
      inspected_by_avatar: el.evaluator_avatar || null,
      inspected_on: el.evaluated_on || null,
      reason: el.reason || null,
      microlearning: null,
      microlearning_video_id: null,
    }));
    inspectionResponsibilityCheckpoints.value = responsibilityCheckpoints;
    inspectionChecklistItems.value = checklistItems;
    showInspectionPane.value = true;
  } catch (error) {
    console.error("Error opening inspection:", error);
    proxy.$notifications?.error("Error opening inspection");
  } finally {
    proxy.$loader?.hide();
  }
};

const addForceBadgeData = async (res) => {
  try {
    isAssigningBadge.value = true;
    proxy.$loader.setAppLoading(true);
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    const payload = {
      employee: res.employee.id,
      responsibility: props.responsibilityId,
      level: res.radioVal,
      forced: true,
    };

    await createProficiencyBadge(accountId, payload);

    await getBadges();
    emit("badgeCountsUpdated");

    showBadgeDialog.value = false;
    updateEmployeeList();
  } catch (error) {
    console.error("Error adding badge:", error);
    if (error.response && error.response.data && error.response.data.non_field_errors) {
      showBadgeDialog.value = false;
    }
  } finally {
    isAssigningBadge.value = false;
    proxy.$loader.setAppLoading(false);
  }
};

const getProficiencyLevelNameFromNumber = (el) => {
  if (el.proficiency_level === 1) return "Apprentice";
  if (el.proficiency_level === 2) return "Professional";
  if (el.proficiency_level === 3) return "Coach";
  if (el.proficiency_level === 4) return "Master";
  return "Unknown";
};

const updateChangeEvaluationStatus = (data) => {
  if (data.evaluation_status == "not_applicable") return "Not Applicable";
  if (data.evaluation_status == "passed") return "Passed";
  if (data.evaluation_status == "failed") return "Failed";
  if (data.evaluation_status == "reset") return "Reset Pending Evaluation";
  return "Pending Evaluation";
};

function handleSkipAwardBadge() {
  showAwardBadgeModal.value = false;
  awardBadgeLevel.value = null;
}

function handleCloseAwardBadgeDialog() {
  showAwardBadgeModal.value = false;
  awardBadgeLevel.value = null;
}

const loadListActiveCheckpointsEvalV2 = async (data) => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const responsibilityData = props.responsibilityData;
    const limit = responsibilityData?.active_checkpoints_count || activeCheckPoints.value || 0;
    const offset = 0;

    const response = await getListActiveCheckpointsEvalV2(
      accountId,
      props.responsibilityId,
      data.employee.id,
      limit,
      offset
    );

    if (response && response.data) {
      checkpointsData.value =
        response.data?.results.map((el) => {
          const newEl = {};
          newEl.id = el.checkpoint;
          newEl.checkpoint_id = el.checkpoint;
          newEl.level = el.proficiency_level;
          newEl.level_name = getProficiencyLevelNameFromNumber(el);
          newEl.criteria = el.checkpoint_criteria;
          newEl.instructions = el.checkpoint_instructions;
          newEl.status = el.status;
          newEl.evaluation_status = el.status;
          newEl.change_evaluation_status = updateChangeEvaluationStatus({
            evaluation_status: el.status,
          });
          newEl.failed_by = el.evaluated_by?.name || null;
          newEl.failed_on = el.evaluated_on || null;
          newEl.failed_reason = el.reason || null;
          newEl.reset_by = el.reset_by || null;
          newEl.reset_on = el.reset_on || "";
          newEl.reset_reason = "";
          newEl.updated_on = el.evaluated_on || null;
          newEl.updated_by = el.evaluated_by
            ? {
                ...el.evaluated_by,
                picture: el.evaluator_avatar,
              }
            : null;
          newEl.avatar = el.evaluator_avatar || null;
          return newEl;
        }) || [];
    }
  } catch (error) {
    console.error("Error loading checkpoints:", error);
    checkpointsData.value = [];
  }
};

const openEvalResponse = async (data) => {
  try {
    showBadgeDialog.value = false;
    proxy.$loader.setAppLoading(true);

    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    const responsibilityData = props.responsibilityData;
    evalModalHeaderState.value = {
      responsibilityCode: normalizedResponsibilityCodeForBadgePick(responsibilityData),
      responsibilityName: responsibilityData?.name || "",
      roleCode: "#245",
      roleName: "Implement CAPEX initiatives",
      positionCode: "#4567",
      positionName: "General Manager",
    };

    let employeeBadges = [];
    try {
      const badgesResponse = await getEmployeeBadges(accountId, data.employee.id);
      if (badgesResponse && badgesResponse.data) {
        employeeBadges = badgesResponse.data.results || [];
      }
    } catch (error) {
      console.error("Error fetching employee badges:", error);
    }

    const employeeCurrentBadge = pickBadgeForResponsibility(
      employeeBadges,
      props.responsibilityId,
      responsibilityMatchContextFromDetail(responsibilityData)
    );

    const emp = data.employee;
    const empId = emp.id;
    const firstName = emp.firstName || emp.first_name || "";
    const lastName = emp.lastName || emp.last_name || "";
    const fullName = emp.fullName || `${firstName} ${lastName}`.trim();

    evalModalEmpState.value = {
      id: empId,
      firstName: firstName,
      lastName: lastName,
      position: emp.title || emp.position || "",
      photoUrl: emp.avatar || emp.photoUrl || "",
      availability: 1,
      isAllocated: false,
      access: emp.access || {},
      badgeList: employeeBadges.map((badge) => ({
        id: badge.id,
        issued_to: badge.issued_to,
        is_current: badge.is_current,
      })),
      currentEmployeeBadge: employeeCurrentBadge
        ? {
            id: employeeCurrentBadge.id,
            level: employeeCurrentBadge.level,
            status: employeeCurrentBadge.status,
          }
        : null,
      user: emp.user || null,
    };

    await loadListActiveCheckpointsEvalV2({ employee: { id: empId } });

    openEvalResponsibility.value = true;
  } catch (error) {
    console.error("Error opening evaluation modal:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const updateEvaluationCheckPointData = async () => {
  if (evalModalEmpState.value) {
    await loadListActiveCheckpointsEvalV2({ employee: evalModalEmpState.value });
  }
};

const getResponsibilityBadges = async () => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const response = await getListofResponsibilityBadge(
      accountId,
      props.responsibilityId,
      "responsibility",
      null
    );

    if (response && response.data && response.data.results) {
      const dataResponsibilitiesBadge = response.data.results;
      badgeFrameItems.value = dataResponsibilitiesBadge
        .filter((badge) => badge.issued_to)
        .map((badge) => {
          let statusName = "";
          if (badge.status === "good_standing") {
            statusName = "Good Standing";
          } else if (badge.status === "forced" || badge.status === "given") {
            statusName = "Given";
          } else {
            statusName = "Obsolete";
          }
          return {
            id: badge.id,
            proficiencylevel: badge.level,
            avatar: badge.issued_to?.avatar || "",
            firstName: badge.issued_to?.first_name || "",
            lastName: badge.issued_to?.last_name || "",
            dot: statusName,
            empId: badge.issued_to?.id || null,
            is_current: badge.is_current,
            expires_when_obsolete: badge.expires_when_obsolete,
            removed_by: badge.removed_by,
          };
        })
        .filter((item) => item !== undefined);
    }
  } catch (error) {
    console.error("Error fetching responsibility badges:", error);
  }
};

const grantOrForceBadge = (badgeDataParam) => {
  openEvalResponsibility.value = false;
  showBadgeConfirmationDialog.value = true;
  badgeData.value = badgeDataParam;
};

const onClick = async (isNotify = false) => {
  showBadgeConfirmationDialog.value = false;
  openEvalResponsibility.value = false;
  proxy.$loader.show();
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    if (badgeData.value?.currentBadge) {
      await deleteResponsibilityBadge(accountId, badgeData.value.currentBadge, {
        badge: badgeData.value.currentBadge,
        method: "upgraded",
        reason: "",
      });
    }

    await createProficiencyBadge(accountId, {
      ...badgeData.value.payload,
      notify: isNotify,
    });

    await getBadges();
    await refreshEvalModalBadgeFrameIfOpen();
    emit("badgeCountsUpdated");
    updateEmployeeList();

    if (evalModalEmpState.value) {
      evalModalEmpState.value = {
        ...evalModalEmpState.value,
        badgeList: badgeFrameItems.value,
      };
    }
  } catch (error) {
    console.error("Error in onClick:", error);
  } finally {
    proxy.$loader.setAppLoading(false);
    proxy.$loader.hide();
  }
};

const removeBadgeByResponsibilityId = (badgeDataParam) => {
  if (badgeDataParam?.id) {
    badgeToRemove.value = badgeDataParam;
    removalReason.value = "";
    showReasonModal.value = true;
  }
};

const handleCloseReasonModal = () => {
  showReasonModal.value = false;
  badgeToRemove.value = null;
  removalReason.value = "";
};

const handleConfirmRemovalWithReason = async () => {
  if (!badgeToRemove.value?.id || !removalReason.value?.trim()) {
    proxy.$notifications?.error("Please provide a reason for removing the badge");
    return;
  }

  try {
    proxy.$loader.setAppLoading(true);
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    await deleteResponsibilityBadge(accountId, badgeToRemove.value.id, {
      badge: badgeToRemove.value.id,
      method: "removed",
      reason: removalReason.value.trim(),
    });

    showReasonModal.value = false;
    openEvalResponsibility.value = false;

    await getBadges();
    await refreshEvalModalBadgeFrameIfOpen();
    emit("badgeCountsUpdated");

    updateEmployeeList();

    if (evalModalEmpState.value) {
      evalModalEmpState.value = {
        ...evalModalEmpState.value,
        badgeList: badgeFrameItems.value,
      };
    }

    proxy.$notifications?.success("Badge removed successfully");

    // Reset state
    badgeToRemove.value = null;
    removalReason.value = "";
  } catch (error) {
    console.error("Error removing badge:", error);
    proxy.$notifications?.error("Error removing badge");
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const reloadBadgeHolderItems = async () => {
  proxy.$loader.show();
  await getBadges();
  proxy.$loader.hide();
};

const getBadgeLevelByLabel = (level) => {
  if (level == null || level === "") return 0;
  if (typeof level === "number" && level >= 0 && level <= 4) return level;
  const l = normalizeBadgeLevel(String(level));
  const map = { novice: 0, apprentice: 1, proficient: 2, coach: 3, architect: 4 };
  return map[l] ?? 0;
};

const getBadgeLevel = (level) => {
  switch (level) {
    case 1:
      return "Apprentice";
    case 2:
      return "Professional";
    case 3:
      return "Coach";
    case 4:
      return "Master";
    default:
      return "Unknown";
  }
};

const upgradeBadge = async (upgradeData) => {
  try {
    badgeData.value = { payload: upgradeData?.payload?.newBadge };
    await onClick(false);
  } catch (error) {
    console.error("Error upgrading badge:", error);
  }
};

const showAwardBadge = (obj) => {
  openEvalResponsibility.value = false;
  showAwardBadgeModal.value = true;
  awardBadgeLevel.value = obj?.level;
};

const awardBadge = async (isNotify = false) => {
  showAwardBadgeModal.value = false;
  proxy.$loader.show();
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const data = {
      employee: evalModalEmpState.value?.id,
      responsibility: props.responsibilityId,
      level: getBadgeLevelByLabel(awardBadgeLevel.value),
      given: false,
      issued_method: "awarded",
      notify: isNotify,
    };

    await createProficiencyBadge(accountId, data);

    showAwardBadgeModal.value = false;
    await getBadges();
    await refreshEvalModalBadgeFrameIfOpen();
    emit("badgeCountsUpdated");
    updateEmployeeList();

    if (evalModalEmpState.value) {
      evalModalEmpState.value = {
        ...evalModalEmpState.value,
        badgeList: badgeFrameItems.value,
      };
    }
  } catch (error) {
    console.error("Error awarding badge:", error);
  } finally {
    proxy.$loader.hide();
  }
};

const updateEmployeeList = () => {
  badgeEmployeeData.value = [];
  employeeModalRenderKey.value++;
};

// Right pane handlers
const closeInspectionPane = () => {
  showInspectionPane.value = false;
  inspectionEmployee.value = null;
  inspectionResponsibilityAssignment.value = null;
  inspectionResponsibilityCheckpoints.value = [];
  inspectionChecklistItems.value = [];
  currentInspectionEmployeeId.value = null;
  inspectionCurrentBadgeLevel.value = null;
  inspectionAwardDialogRequest.value = null;
  showGiveBadgeDialogFromInspection.value = false;
  giveBadgeDialogLockedLevel.value = null;
  giveBadgeDialogDefaultLevel.value = "apprentice";
};

const handleInspectionBadgeExpirationSynced = async () => {
  await getBadges();
  await refreshEvalModalBadgeFrameIfOpen();
  const empId = currentInspectionEmployeeId.value;
  const resId = props.responsibilityId;
  if (empId && resId) {
    await reloadInspectionCheckpoints(empId, resId, {
      skipInspectionBadgeSync: false,
    });
  }
  emit("badgeCountsUpdated");
  updateEmployeeList();
};

const handleBadgeAwarded = async (payload) => {
  const isObj = payload && typeof payload === "object";
  if (isObj && showInspectionPane.value && inspectionEmployee.value) {
    inspectionEmployee.value = {
      ...inspectionEmployee.value,
      badge: {
        id: payload.badgeId ?? inspectionEmployee.value.badge?.id ?? null,
        level: payload.level ?? inspectionEmployee.value.badge?.level,
        dot: payload.dot ?? inspectionEmployee.value.badge?.dot ?? "Good Standing",
      },
    };
    if (payload.level != null) {
      inspectionCurrentBadgeLevel.value = normalizeProficiencyLevelForPicker(payload.level);
    }
  }
  await getBadges();
  await refreshEvalModalBadgeFrameIfOpen();
  const empId = isObj ? payload.employeeId : currentInspectionEmployeeId.value;
  const resId = isObj ? payload.responsibilityId : props.responsibilityId;
  if (empId && resId) {
    await reloadInspectionCheckpoints(empId, resId, {
      skipInspectionBadgeSync: true,
    });
  }
  emit("badgeCountsUpdated");
  updateEmployeeList();
};

/** Single GET + pick â€” matches NewEmployeePathToReadinessView.syncInspectionPaneBadgeFromApi (no retry storm). */
const syncInspectionEmployeeBadgeFromApi = async (accountId, empId, resId, options = {}) => {
  const { preserveLocalBadgeOnMiss = false, matchContext: matchContextOverride } = options;
  const previousBadge = inspectionEmployee.value?.badge;
  const matchContext = matchContextOverride ?? inspectionBadgeMatchContext();
  try {
    const { data } = await getEmployeeBadges(accountId, empId);
    const results = data?.results || [];
    const currentBadge = pickBadgeForResponsibility(results, resId, matchContext);
    let nextBadge = null;
    if (currentBadge) {
      nextBadge = mapBadgeRowToInspectionChip(currentBadge);
    } else if (preserveLocalBadgeOnMiss && previousBadge) {
      const hasId = previousBadge.id != null && previousBadge.id !== "";
      const lvl = previousBadge.level != null ? String(previousBadge.level).toLowerCase() : "";
      const hasRealLevel = lvl !== "" && lvl !== "novice";
      if (hasId || hasRealLevel) {
        nextBadge = previousBadge;
      }
    }
    if (inspectionEmployee.value) {
      inspectionEmployee.value = {
        ...inspectionEmployee.value,
        badge: nextBadge,
      };
    }
    inspectionCurrentBadgeLevel.value = normalizeProficiencyLevelForPicker(
      currentBadge?.level ?? nextBadge?.level
    );
  } catch (e) {
    console.error("Error syncing inspection employee badge:", e);
  }
};

const reloadInspectionCheckpoints = async (empIdOverride, resIdOverride, options = {}) => {
  const { skipInspectionBadgeSync = false, ...syncOptions } = options;
  const empId = empIdOverride ?? currentInspectionEmployeeId.value;
  const resId = resIdOverride ?? props.responsibilityId;
  if (!empId || !resId) return;
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const evalResponse = await getListActiveCheckpointsEvalV2(accountId, resId, empId, 100, 0);
    const evalResults = evalResponse?.data?.results || [];
    const toTitleCaseStatus = (s) => {
      if (!s) return "Pending";
      return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };
    const levelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
    inspectionChecklistItems.value = evalResults.map((el) => ({
      id: el.checkpoint,
      // Inspection PK — the record checkpoint_evaluation chatter is keyed on.
      evaluation_id: el.id,
      checkpoint_id: el.checkpoint,
      responsibility_id: resId,
      employee_id: empId,
      level: levelMap[el.proficiency_level] || "Unknown",
      prefix: el.prefix || "",
      checkpoint: el.checkpoint_criteria || "",
      status: toTitleCaseStatus(el.status),
      inspected_by: el.evaluated_by?.name || null,
      inspected_by_avatar: el.evaluator_avatar || null,
      inspected_on: el.evaluated_on || null,
      reason: el.reason || null,
      microlearning: null,
      microlearning_video_id: null,
    }));
    if (!skipInspectionBadgeSync) {
      await syncInspectionEmployeeBadgeFromApi(accountId, empId, resId, syncOptions);
    }
  } catch (error) {
    console.error("Error reloading checkpoints:", error);
  }
};

function normalizeInspectionAwardLevel(obj) {
  const raw = obj?.level != null ? String(obj.level) : obj?.value != null ? String(obj.value) : "";
  const level = normalizeBadgeLevel(raw);
  if (level === "novice") return "apprentice";
  if (["apprentice", "proficient", "coach", "architect"].includes(level)) {
    return level;
  }
  return "apprentice";
}

function getCurrentInspectionPaneBadgeLevelNumber() {
  const b = inspectionEmployee.value?.badge;
  if (!b || b.level == null) return 0;
  if (typeof b.level === "number") {
    return b.level >= 1 && b.level <= 4 ? b.level : 0;
  }
  return getBadgeLevelByLabel(b.level);
}

function isLevelCompleteForAwardLocal(level) {
  return isLevelCompleteForAward(level, inspectionChecklistItems.value || []);
}

function shouldShowAwardBadgeForLevel(level) {
  if (!isLevelCompleteForAwardLocal(level)) return false;
  const currentLevelNum = getCurrentInspectionPaneBadgeLevelNumber();
  const levelStr = typeof level === "string" ? level : level && String(level);
  const awardLevelNum = getBadgeLevelByLabel(levelStr || "novice");
  return awardLevelNum > 0 && awardLevelNum > currentLevelNum;
}

const handleCheckpointPass = async (item) => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const chkpId = item.id || item.checkpoint_id;
    await passCheckPointEvaluation(accountId, item.responsibility_id, chkpId, {
      employee: item.employee_id,
      checkpoint: chkpId,
      reason: "passed",
      status: "passed",
    });
    await reloadInspectionCheckpoints();
    await nextTick();
    let levelToShow = null;
    if (item?.level && shouldShowAwardBadgeForLevel(item.level)) {
      levelToShow = item.level;
    }
    if (levelToShow) {
      inspectionAwardDialogRequest.value = {
        token: Date.now(),
        level: normalizeInspectionAwardLevel({ level: levelToShow }),
      };
    }
  } catch (error) {
    const msg = proficiencyCheckpointErrorMessage(error) || "Error passing checkpoint";
    proxy.$notifications.error(msg);
    console.error("Error passing checkpoint:", error);
  }
};

const handleCheckpointFail = (item) => {
  pendingCheckpointReasonItem.value = item;
  checkpointReasonModalType.value = "fail";
  checkpointReasonText.value = "";
  showCheckpointReasonModal.value = true;
};

const handleCheckpointNotApplicable = (item) => {
  pendingCheckpointReasonItem.value = item;
  checkpointReasonModalType.value = "notApplicable";
  checkpointReasonText.value = "";
  showCheckpointReasonModal.value = true;
};

const handleCheckpointClose = async (item) => {
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const chkpId = item.id || item.checkpoint_id;
    await resetCheckPointEvaluation(accountId, item.responsibility_id, chkpId, {
      employee: item.employee_id,
      checkpoint: chkpId,
      status: "reset",
    });
    await reloadInspectionCheckpoints();
  } catch (error) {
    console.error("Error resetting checkpoint:", error);
  }
};

const closeCheckpointReasonModal = () => {
  showCheckpointReasonModal.value = false;
  pendingCheckpointReasonItem.value = null;
  checkpointReasonText.value = "";
};

const confirmCheckpointReason = async () => {
  if (!pendingCheckpointReasonItem.value || !checkpointReasonText.value?.trim()) return;
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const item = pendingCheckpointReasonItem.value;
    const isFail = checkpointReasonModalType.value === "fail";
    const chkpId = item.id || item.checkpoint_id;
    const payload = {
      employee: item.employee_id,
      checkpoint: chkpId,
      reason: checkpointReasonText.value.trim(),
      status: isFail ? "failed" : "not_applicable",
    };
    if (isFail) {
      await failCheckPointEvaluation(accountId, item.responsibility_id, chkpId, payload);
    } else {
      await notApplicableCheckPointEvaluation(accountId, item.responsibility_id, chkpId, payload);
    }
    closeCheckpointReasonModal();
    await reloadInspectionCheckpoints();
    if (isFail) {
      proxy.$notifications?.success("Checkpoint marked as failed");
    } else {
      proxy.$notifications?.success("Checkpoint marked as not applicable");
    }
  } catch (error) {
    closeCheckpointReasonModal();
    const msg = proficiencyCheckpointErrorMessage(error) || "Error evaluating checkpoint";
    proxy.$notifications.error(msg);
    console.error("Error evaluating checkpoint:", error);
  }
};

async function resolveBadgePayloadForInspectionRemove() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const empId = currentInspectionEmployeeId.value;
  const resId = props.responsibilityId;
  if (!accountId || !empId || !resId) return null;
  try {
    const badgesResponse = await getEmployeeBadges(accountId, empId);
    const employeeBadges = badgesResponse?.data?.results || [];
    const row = pickBadgeForResponsibility(employeeBadges, resId, inspectionBadgeMatchContext());
    if (!row) return null;
    return { id: row.id, level: row.level, currentBadge: row };
  } catch (e) {
    console.warn("resolveBadgePayloadForInspectionRemove:", e);
    return null;
  }
}

async function processRemoveBadgeFromInspection(data) {
  try {
    let payload = data && typeof data === "object" ? { ...data } : { level: data };
    let id = payload.id != null && payload.id !== "" ? payload.id : null;
    if (!id) {
      id = inspectionEmployee.value?.badge?.id ?? null;
    }
    if (!id) {
      const resolved = await resolveBadgePayloadForInspectionRemove();
      if (resolved?.id != null && resolved.id !== "") {
        id = resolved.id;
        payload = {
          ...payload,
          id: resolved.id,
          level: payload.level ?? resolved.level,
          currentBadge: payload.currentBadge ?? resolved.currentBadge,
        };
      }
    }
    if (!id) {
      proxy.$notifications?.error(
        "Could not resolve badge to remove. Try again after the list loads."
      );
      return;
    }

    const badgeLevel =
      payload.level || payload.currentBadge?.level || inspectionEmployee.value?.badge?.level || "";
    const badgeName = typeof badgeLevel === "string" ? badgeLevel : getBadgeLevelName(badgeLevel);

    selectedBadgeForRemoval.value = {
      id,
      badgeName,
      level: badgeLevel,
      employeeName: inspectionEmployee.value?.fullName || "",
    };
    removeBadgeDialogFromInspection.value = !!showInspectionPane.value;
    showRemoveBadgeDialog.value = true;
  } catch (e) {
    console.error(e);
    proxy.$notifications?.error("Error preparing badge removal");
  }
}

function onGiveBadgeDialogInput(visible) {
  showGiveBadgeDialog.value = visible;
  if (!visible) {
    giveBadgeDialogLockedLevel.value = null;
    giveBadgeDialogDefaultLevel.value = "apprentice";
    showGiveBadgeDialogFromInspection.value = false;
  }
}

const handleInspectionIssueBadge = (event) => {
  if (event?.isRemoveAction) {
    processRemoveBadgeFromInspection(inspectionEmployee.value?.badge || event);
    return;
  }
  const level = (event?.value || event?.level || "apprentice").toString().toLowerCase();
  const grandLabel = String(event?.grand_label || "").toLowerCase();
  if (grandLabel.startsWith("award")) {
    inspectionAwardDialogRequest.value = {
      token: Date.now(),
      level: normalizeInspectionAwardLevel(event),
    };
    return;
  }
  showGiveBadgeDialogFromInspection.value = true;
  giveBadgeDialogLockedLevel.value = level;
  giveBadgeDialogDefaultLevel.value = level;
  showGiveBadgeDialog.value = true;
};

const confirmGiveBadge = async ({ level, notifyEmployee }) => {
  try {
    proxy.$loader?.show();
    showGiveBadgeDialog.value = false;
    giveBadgeDialogLockedLevel.value = null;
    giveBadgeDialogDefaultLevel.value = "apprentice";
    showGiveBadgeDialogFromInspection.value = false;
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const levelIntMap = { apprentice: 1, proficient: 2, coach: 3, architect: 4 };
    const levelNum = levelIntMap[normalizeBadgeLevel(level)] ?? level;
    const levelLabelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
    const payload = {
      employee: currentInspectionEmployeeId.value,
      responsibility: props.responsibilityId,
      level: levelNum,
      forced: true,
      notify: !!notifyEmployee,
    };
    const createRes = await createProficiencyBadge(accountId, payload);
    const newId = extractCreatedProficiencyBadgeId(createRes);
    const levelLabel =
      typeof levelNum === "number" && levelLabelMap[levelNum]
        ? levelLabelMap[levelNum]
        : String(level || "apprentice").replace(/\b\w/g, (c) => c.toUpperCase());
    if (showInspectionPane.value && inspectionEmployee.value) {
      inspectionEmployee.value = {
        ...inspectionEmployee.value,
        badge: {
          id: newId ?? inspectionEmployee.value.badge?.id ?? null,
          level: levelLabel,
          dot: "Given",
        },
      };
    }
    inspectionCurrentBadgeLevel.value = normalizeProficiencyLevelForPicker(levelLabel);
    await getBadges();
    emit("badgeCountsUpdated");
    updateEmployeeList();
    await reloadInspectionCheckpoints(undefined, undefined, {
      skipInspectionBadgeSync: true,
    });
    proxy.$notifications?.success("Badge issued successfully");
  } catch (error) {
    console.error("Error issuing badge:", error);
    proxy.$notifications?.error("Error issuing badge");
  } finally {
    proxy.$loader?.hide();
  }
};

const confirmUpgradeBadge = async ({ level, notifyEmployee }) => {
  try {
    proxy.$loader?.show();
    showUpgradeBadgeDialog.value = false;
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const levelIntMap = { apprentice: 1, proficient: 2, coach: 3, architect: 4 };
    const levelNum = levelIntMap[normalizeBadgeLevel(level)] ?? level;
    const levelLabelMap = { 1: "Apprentice", 2: "Professional", 3: "Coach", 4: "Master" };
    const payload = {
      employee: currentInspectionEmployeeId.value,
      responsibility: props.responsibilityId,
      level: levelNum,
      forced: true,
      notify: notifyEmployee !== undefined ? notifyEmployee : true,
    };
    const createRes = await createProficiencyBadge(accountId, payload);
    const newId = extractCreatedProficiencyBadgeId(createRes);
    const levelLabel =
      typeof levelNum === "number" && levelLabelMap[levelNum]
        ? levelLabelMap[levelNum]
        : String(level || "apprentice").replace(/\b\w/g, (c) => c.toUpperCase());
    if (showInspectionPane.value && inspectionEmployee.value) {
      inspectionEmployee.value = {
        ...inspectionEmployee.value,
        badge: {
          id: newId ?? inspectionEmployee.value.badge?.id ?? null,
          level: levelLabel,
          dot: "Given",
        },
      };
    }
    inspectionCurrentBadgeLevel.value = normalizeProficiencyLevelForPicker(levelLabel);
    await getBadges();
    emit("badgeCountsUpdated");
    updateEmployeeList();
    await reloadInspectionCheckpoints(undefined, undefined, {
      skipInspectionBadgeSync: true,
    });
    proxy.$notifications?.success("Badge upgraded successfully");
  } catch (error) {
    console.error("Error upgrading badge:", error);
    proxy.$notifications?.error("Error upgrading badge");
  } finally {
    proxy.$loader?.hide();
  }
};

watch(
  () => badgeHolders.paginationDefaultParams.offset,
  () => {
    getBadges();
  }
);

watch(
  () => badgeHolders.paginationDefaultParams.limit,
  () => {
    badgeHolders.setPaginationDataOffset(0);
  }
);

watch(
  () => viewState.state.filters.searchText,
  () => {
    getBadges();
  }
);

watch(
  () => badgeHolders.orderingData.field,
  () => {
    getBadges();
  }
);

async function fetchBadgeChatter() {
  const badge = selectedBadgeChatter.value;
  if (badgeChatterLoading.value || !badge || !badge.id) return;
  badgeChatterLoading.value = true;
  try {
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const { data } = await getChatterList(accountId, badge.id, "badge", badgeChatterPage.value);
    if (data && data.count) totalBadgeChatter.value = data.count;
    if (data && Array.isArray(data.results)) {
      const items = data.results.map((el) => {
        const ms = new Date(el.recorded_on).getTime().toString();
        const nameParts = el.recorded_by_name ? el.recorded_by_name.split(" ") : [""];
        const item = {
          id: el.id,
          IsExIQtiveBot: false,
          message: formatOrganizationEmployeeChatterNote(el.note),
          created_on: ms,
          IsHistory: el.is_history,
        };
        item.avatar =
          nameParts.length >= 2
            ? { picture: "", firstName: nameParts[0], lastName: nameParts.slice(1).join(" ") }
            : { picture: "", firstName: el.recorded_by_name || "", lastName: "" };
        return item;
      });
      badgeChatterData.value.push(...items);
    }
  } catch (error) {
    console.error("Error fetching badge chatter:", error);
  } finally {
    badgeChatterLoading.value = false;
  }
}

async function handleBadgeChatterClick({ item } = {}) {
  if (!item || !item.id) return;
  selectedBadgeChatter.value = item;
  badgeChatterData.value = [];
  badgeChatterPage.value = 1;
  totalBadgeChatter.value = 0;
  showBadgeChatterDrawer.value = true;
  await fetchBadgeChatter();
}

function closeBadgeChatterDrawer() {
  showBadgeChatterDrawer.value = false;
  selectedBadgeChatter.value = null;
  badgeChatterData.value = [];
  badgeChatterPage.value = 1;
  totalBadgeChatter.value = 0;
}

async function loadMoreBadgeChatter() {
  if (badgeChatterLoading.value) return;
  badgeChatterPage.value += 1;
  await fetchBadgeChatter();
}

async function handleToggleBadgeStatus({ nextStatus } = {}) {
  const badge = selectedBadgeChatter.value;
  if (!badge) return;
  const badgeId = badge.id;
  const ok =
    nextStatus === "obsolete"
      ? await proficiencyBadgeExpiration.markObsolete(badgeId, badgeId)
      : await proficiencyBadgeExpiration.restoreGoodStanding(badgeId, badgeId);
  if (!ok) {
    proxy?.$notifications?.error?.(
      nextStatus === "obsolete"
        ? "Failed to force badge to obsolete"
        : "Failed to restore badge to good standing"
    );
    return;
  }
  closeBadgeChatterDrawer();
  await getBadges();
  emit("badgeCountsUpdated");
}

function closeInspection() {
  closeInspectionPane();
  if (coachInspect.showInspectChecklistView.value) coachInspect.closeInspectChecklistView();
}

defineExpose({
  closeInspection,
});
</script>

<style scoped>
.responsibility-badge-holders-tab__actions {
  display: flex;
  justify-content: flex-end;
  padding: 0 16px;
}
.inspect-form-page-wrapper {
  width: 100%;
  margin-top: 16px;
}
::v-deep .minimalistic-readiness-level.Coach {
  border: 1px solid #00a3ff;
  background: #effeff;
}

::v-deep .minimalistic-readiness-level.Master {
  border: 1px solid #2e1ec1;
  background: #f2f3ff;
}

::v-deep .minimalistic-readiness-level.Apprentice {
  border: 1px solid #c17200;
  background: #fff5d3;
}

::v-deep .minimalistic-readiness-level.Professional {
  border: 1px solid #00b813;
  background: #f3fff9;
}
</style>
